import { UserSession } from "@/types/session";
import { useCallback, useRef, useState } from "react";
import { SessionContext } from "./SessionContext";
import { sessionService } from "@/services/sessionService";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { ChatEntry } from "@/types/chat";

export type SessionsData = Record<ChatEntry, UserSession> | null;
export type ClosedSessionsData = Record<UserSession["id"], UserSession> | null;

export const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();

  const [sessions, setSessions] = useState<SessionsData>(null);
  const [closedSessions, setClosedSessions] = useState<ClosedSessionsData>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingClosedSessions, setIsLoadingClosedSessions] = useState(false);
  const isPendingRef = useRef(isLoading);

  const getSession = useCallback(
    async (chatMode: ChatEntry, closeIfExpired: boolean = false): Promise<UserSession | null | undefined> => {
      if (!isAuthenticated) return undefined;
      try {
        setIsLoading(true);
        const session = await sessionService.getCurrentChatSession(chatMode);

        if (closeIfExpired && session && new Date(session.expires_at).getTime() < Date.now()) {
          await sessionService.closeSession(session.id);
          setSessions((prev) => ({ ...(prev ?? {}), [chatMode]: null }));
          return null;
        }

        setSessions((prev) => ({ ...(prev ?? {}), [chatMode]: session }));
        return session;
      } catch (error) {
        console.error("Error getting chat session", error);
        toast({
          title: "Error getting chat session",
          description: "Please try again later",
          variant: "destructive",
        });
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [toast, isAuthenticated]
  );

  const loadClosedSessions = useCallback(async (limit: number = 10): Promise<UserSession[]> => {
    if (!isAuthenticated) {
      setClosedSessions(null);
      return [];
    }
    setIsLoadingClosedSessions(true);
    try {
      const sessions = await sessionService.getHistorySessions(limit);
      setClosedSessions(sessions.reduce((acc, session) => ({ ...acc, [session.id]: session }), {}));
      return sessions;
    } catch (error) {
      console.error("Error loading closed sessions", error);
      return [];
    } finally {
      setIsLoadingClosedSessions(false);
    }
  }, [isAuthenticated]);

  const createNewSession = useCallback(
    async (chatMode: ChatEntry) => {
      if (isPendingRef.current) return;
      try {
        setIsLoading(true);
        isPendingRef.current = true;
        const session = await sessionService.createNewSingleSession(chatMode);
        setSessions((prev) => ({ ...(prev ?? {}), [chatMode]: session }));

        toast({
          title: "New Session Started",
          description: `Session ID: ${session.id.slice(0, 8)}...`,
        });

        void loadClosedSessions(40);

        return session;
      } catch (error) {
        console.error("Error creating new session", error);
        toast({
          title: "Error creating new session",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        isPendingRef.current = false;
      }
    },
    [toast, loadClosedSessions]
  );

  const extendSession = useCallback(
    async (sessionId: UserSession["id"]) => {
      if (isPendingRef.current) return;
      try {
        setIsLoading(true);
        isPendingRef.current = true;
        const session = await sessionService.extendSession(sessionId);
        console.log("extended session", session);
        setSessions((prev) => ({ ...(prev ?? {}), [session.chat_mode]: session }));

        toast({
          title: "Session Extended",
          description: `Session extended to ${new Date(session.expires_at).toLocaleString().split(" ")[1]}`,
        });
      } catch (error) {
        console.error("Error extending session", error);
        toast({
          title: "Error extending session",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        isPendingRef.current = false;
      }
    },
    [toast]
  );

  const getClosedUnratedSessions = useCallback(async () => {
    if (isPendingRef.current) return;
    try {
      setIsLoading(true);
      isPendingRef.current = true;
      const sessions = await sessionService.getClosedUnratedSessions(true);
      return sessions;
    } catch (error) {
      console.error("Error getting closed unrated sessions", error);
      return [];
    } finally {
      setIsLoading(false);
      isPendingRef.current = false;
    }
  }, []);

  const getSessionById = useCallback(async (sessionId: UserSession["id"]) => {
    try {
      const session = await sessionService.getSessionById(sessionId);
      return session;
    } catch (error) {
      console.error("Error getting session by id", error);
      return null;
    }
  }, []);

  const renameSession = useCallback(
    async (sessionId: UserSession["id"], sessionName: string | null) => {
      try {
        const updated = await sessionService.renameSession(sessionId, sessionName);
        setClosedSessions((prev) => {
          if (!prev || !(sessionId in prev)) return prev;
          return { ...prev, [sessionId]: updated };
        });
        setSessions((prev) => {
          if (!prev || prev[updated.chat_mode]?.id !== sessionId) return prev;
          return { ...prev, [updated.chat_mode]: updated };
        });
        return updated;
      } catch (error) {
        console.error("Error renaming session", error);
        toast({
          title: "Couldn't rename conversation",
          description: "Try again later.",
          variant: "destructive",
        });
        return null;
      }
    },
    [toast],
  );

  const deleteSession = useCallback(
    async (sessionId: UserSession["id"]) => {
      try {
        await sessionService.deleteSession(sessionId);
        setClosedSessions((prev) => {
          if (!prev || !(sessionId in prev)) return prev;
          const next = { ...prev };
          delete next[sessionId];
          return next;
        });
        setSessions((prev) => {
          if (!prev) return prev;
          let changed = false;
          const next = { ...prev };
          for (const key of Object.keys(next) as Array<keyof typeof next>) {
            if (next[key]?.id === sessionId) {
              next[key] = null as unknown as UserSession;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
        return true;
      } catch (error) {
        console.error("Error deleting session", error);
        toast({
          title: "Couldn't delete conversation",
          description: "Try again later.",
          variant: "destructive",
        });
        return false;
      }
    },
    [toast],
  );

  const reopenSessionFromHistory = useCallback(async (sessionId: UserSession["id"]) => {
    try {
      setIsLoading(true);
      const session = await sessionService.reopenSession(sessionId);
      setSessions((prev) => ({ ...(prev ?? {}), [session.chat_mode]: session }));
      setClosedSessions((prev) => {
        if (!prev || !(sessionId in prev)) return prev;
        return { ...prev, [sessionId]: session };
      });
      return session;
    } catch (error) {
      console.error("Error reopening session", error);
      toast({
        title: "Couldn't open conversation",
        description: "Try again later.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const closeSession = useCallback(
    async (session: UserSession) => {
      if (isPendingRef.current) return;
      try {
        setIsLoading(true);
        isPendingRef.current = true;
        const closed = await sessionService.closeSession(session.id);
        setSessions((prev) => ({ ...(prev ?? {}), [session.chat_mode]: null }));
        setClosedSessions((prev) => ({ ...(prev ?? {}), [closed.id]: closed }));
      } catch (error) {
        console.error("Error closing session", error);
        toast({
          title: "Error closing session",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        isPendingRef.current = false;
      }
    },
    [toast]
  );

  const rateSessions = useCallback(
    async (sessionIds: UserSession["id"][], rating: 0 | 1) => {
      if (isPendingRef.current) return;
      try {
        setIsLoading(true);
        isPendingRef.current = true;
        await sessionService.rateSessions(sessionIds, rating);
        toast({
          title: "Thank you for your feedback!",
        });
      } catch (error) {
        console.error("Error rating sessions", error);
        toast({
          title: "Error rating sessions",
          description: "Please try again later",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        isPendingRef.current = false;
      }
    },
    [toast]
  );

  return (
    <SessionContext.Provider
      value={{
        sessions,
        isLoading,
        isLoadingClosedSessions,
        createNewSession,
        getSession,
        closeSession,
        extendSession,
        getClosedUnratedSessions,
        rateSessions,
        loadClosedSessions,
        closedSessions,
        getSessionById,
        reopenSessionFromHistory,
        renameSession,
        deleteSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
